// The board FX layer's drawing (IMPLEMENTATION §2.6 board FX 60 / Gallery FX 65, §2.9 "Sprites are pooled"):
// every FX sprite record and live particle of the pure BoardFx (render/juice/boardFx.ts) is drawn with a
// pooled image — frame, position, size, angle, alpha, multiply tint, additive blend — and released the frame
// its record is gone. No Phaser tween or emitter is used: the FX clock drives everything, so a frozen clock
// (`__gm.render.freezeAt`) freezes the picture exactly. Counts back `__gm.render.count('fx' | 'galleryFx')`.
import type { BoardFx, FxSprite } from '../../juice/boardFx.ts';
import type { Particle } from '../../juice/particles.ts';
import { DEPTH } from '../../layers.ts';
import type { Spr, SprPool } from '../Spr.ts';

interface Drawn {
  spr: Spr;
  gen: number;
}

export class FxLayer {
  private readonly drawn = new Map<FxSprite | Particle, Drawn>();
  private gen = 0;
  private nGallery = 0;
  private readonly pool: SprPool;

  constructor(pool: SprPool) {
    this.pool = pool;
  }

  private spr(key: FxSprite | Particle, frame: string, depth: number): Spr {
    let d = this.drawn.get(key);
    if (d === undefined) {
      d = { spr: this.pool.acquire(frame, depth), gen: this.gen };
      this.drawn.set(key, d);
    } else {
      this.pool.frame(d.spr, frame);
    }
    d.gen = this.gen;
    return d.spr;
  }

  /** Draws the FX of `fx` as they are now (after `fx.step`). */
  draw(fx: BoardFx): void {
    this.gen += 1;
    let gallery = 0;
    for (const f of fx.sprites) {
      const s = this.spr(f, f.frame, f.depth);
      const k = f.size * f.s;
      s.place(f.x, f.y, k * f.sx, k * f.sy);
      s.angle(f.rot);
      s.alpha(f.a < 0 ? 0 : f.a > 1 ? 1 : f.a);
      s.setTint(f.tint);
      s.blend(f.add);
      if (f.gallery) gallery += 1;
    }
    for (const p of fx.particles.list()) {
      const s = this.spr(p, p.frame, p.depth);
      s.place(p.x, p.y, p.s, p.s);
      s.angle(p.rot);
      s.alpha(p.a < 0 ? 0 : p.a > 1 ? 1 : p.a);
      s.setTint(p.tint);
      s.blend(p.add);
      if (p.depth >= DEPTH.galleryFx) gallery += 1;
    }
    this.nGallery = gallery;
    if (this.drawn.size > fx.sprites.length + fx.particles.alive) {
      for (const [key, d] of this.drawn) {
        if (d.gen === this.gen) continue;
        this.pool.release(d.spr);
        this.drawn.delete(key);
      }
    }
  }

  /** Releases every drawn FX image (a rebuild). */
  clear(): void {
    for (const d of this.drawn.values()) this.pool.release(d.spr);
    this.drawn.clear();
    this.nGallery = 0;
  }

  /** FX images drawn (sprites + particles). */
  get count(): number {
    return this.drawn.size;
  }

  /** Of those, on the Gallery FX layer (depth ≥ 65). */
  get galleryCount(): number {
    return this.nGallery;
  }
}
